import axios from "axios";
import { getAdminToken, clearAdminToken } from "@/lib/adminAuth";
import { getShopToken, clearShopToken } from "@/lib/shopAuth";
import { getPmToken, clearPmToken } from "@/lib/pmAuth";
import { getDevToken, clearDevToken } from "@/lib/devAuth";
import { getJwt, clearJwt } from "@/lib/jwtAuth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
  // No credentials — auth is sent via the Authorization / X-Admin-Token
  // headers so browsers don't reject the wildcard CORS that Cloudflare /
  // Emergent ingress applies. (See lib/jwtAuth.js for the rationale.)
  withCredentials: false,
  // Photos as base64 can be large — bump limits
  maxContentLength: 50 * 1024 * 1024,
  maxBodyLength: 50 * 1024 * 1024,
});

// Attach Crew-Hub JWT, Safety-Admin token, and Shop token to every request.
api.interceptors.request.use((config) => {
  const adminTok = getAdminToken();
  if (adminTok) {
    config.headers["X-Admin-Token"] = adminTok;
  }
  const shopTok = getShopToken();
  if (shopTok) {
    config.headers["X-Shop-Token"] = shopTok;
  }
  const pmTok = getPmToken();
  if (pmTok) {
    config.headers["X-PM-Token"] = pmTok;
  }
  const devTok = getDevToken();
  if (devTok) {
    config.headers["X-Dev-Token"] = devTok;
  }
  const jwt = getJwt();
  if (jwt && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${jwt}`;
  }
  return config;
});

// On 401, only clear the token that matches the URL scope being called.
// Previously this cleared every token attached to the request, which broke
// roles like Superintendent (super@) — they hold admin + pm + shop tokens
// simultaneously, and a single 401 from `/api/dev/check` (which they never
// have a dev token for) wiped their admin token and bounced them to the
// PM login screen on the very next protected click.
//
// Scope-matching avoids that footgun:
//   /api/admin/...  → admin token only
//   /api/pm/...     → pm token only
//   /api/shop/...   → shop token only
//   /api/dev/...    → dev token only
//   /api/auth/...   → JWT only
// Any other 401 (rate-limit, generic public endpoint) clears nothing —
// the page-level guard handles redirect cleanly.
api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err?.response?.status === 401) {
      const cfg = err.config || {};
      const url = String(cfg.url || "");
      // Strip the baseURL if axios kept it on cfg.url
      const path = url.replace(/^https?:\/\/[^/]+/, "").replace(/^\/api/, "");

      if (path.startsWith("/admin")) {
        if (cfg.headers?.["X-Admin-Token"]) clearAdminToken();
      } else if (path.startsWith("/pm")) {
        if (cfg.headers?.["X-PM-Token"]) clearPmToken();
      } else if (path.startsWith("/shop")) {
        if (cfg.headers?.["X-Shop-Token"]) clearShopToken();
      } else if (path.startsWith("/dev")) {
        if (cfg.headers?.["X-Dev-Token"]) clearDevToken();
      } else if (path.startsWith("/auth")) {
        if (cfg.headers?.Authorization) clearJwt();
      }
      // else: shared/generic endpoint — leave tokens intact.
    }
    return Promise.reject(err);
  }
);
